import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useReducer, useRef } from "react";
import { useResizeDetector } from "react-resize-detector";
import { g_defaultOverlayNcode, IBrushType, InkStorage, InkStorageEventName, isSamePage, NEO_SMARTPEN_TYPE, PLAYSTATE, sleep, StrokeStatus, ZoomFitEnum } from "../common";
import { MixedPageView } from "../renderer";
import { PDFVIEW_ZOOM_MAX, PDFVIEW_ZOOM_MIN } from "../renderer/RendererConstants";
import { prepareNewOverlayPage } from "./OverlayPageManager";
import ViewOptionsForTest from "./ViewOptionsForTest";
// TODO: 레디스에서 페이지 정보를 받아오도록 하자.
//
// a) 1일 때, 레디스에서 코드를 받아온다. (섹션은 g_defaultOverlayNcode.section)
// b) a에서 받은 sobp를 넣고 overlay를 띄운다. overlaySobp={sobp} 이런식으로.
// b) 2일 때, 선생님이 레디스에 다음과 같이 전달한다.
//     b-1) 오버레이를 띄우는데, 할당된 sobp가 a에서 받은 거다
// c) 학생은, ppt 화면으로 전환되면서 b-1의 sobp를 같이 받아온다.
// d) ppt 화면위에 overlay를 띄울 때 c에서 받은 코드를 인수로 주고 띄운다. overlaySobp={sobp} 이런식으로.
export const OVERLAY_SECTION = g_defaultOverlayNcode.section;
const OVERLAY_OWNER = g_defaultOverlayNcode.owner;
const OVERLAY_BOOK = g_defaultOverlayNcode.book;
const OVERLAY_STARTPAGE = g_defaultOverlayNcode.page;
// eslint-disable-next-line react/display-name
const OverlayInkView = forwardRef((props, ref) => {
    // The component instance will be extended
    // with whatever you return from the callback passed
    // as the second argument
    // https://stackoverflow.com/questions/37949981/call-child-method-from-parent
    useImperativeHandle(ref, () => ({
        clearOverlay() {
            const is = InkStorage.getInstance();
            const strokesAll = is.getPageStrokes(options.current.activePage);
            const filteredStrokes = strokesAll.filter(stroke => (stroke.brushType !== IBrushType.ERASER) &&
                (stroke.status !== StrokeStatus.ERASED));
            filteredStrokes.forEach(s => is.markErased(s));
            // 렌더러에 알린다
            is.dispatcher.dispatch(InkStorageEventName.ON_STROKE_ERASED, {
                inputType: NEO_SMARTPEN_TYPE.NETWORK_PEN,
                erasedStrokes: filteredStrokes,
                // eraserStroke: eraserStroke,
            });
        }
    }));
    /**
     * Testing을 위한 Options
     */
    // const forceUpdate = useForceUpdate();
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const options = useRef({});
    const setOptions = (opt, currentOptions) => {
        Object.keys(opt).forEach(key => {
            currentOptions.current[key] = opt[key];
        });
        forceUpdate();
    };
    const handleOptionChanged = (newOptions) => {
        setOptions(newOptions, options);
    };
    useEffect(() => {
        options.current = Object.assign({}, props);
        if (props.onMounted) {
            props.onMounted({
                getStateFn: getState
            });
        }
    }, []);
    useEffect(() => {
        options.current.pens = props.pens ? [...props.pens] : undefined;
        forceUpdate();
    }, [props.pens]);
    useEffect(() => {
        options.current.disablePanZoom = props.disablePanZoom;
        forceUpdate();
    }, [props.disablePanZoom]);
    useEffect(() => {
        options.current.disableAutoFocus = props.disableAutoFocus;
        forceUpdate();
    }, [props.disableAutoFocus]);
    // useEffect(() => {
    //   options.current.activePage = props.activePage;
    //   forceUpdate();
    // }, [props.activePage]);
    useEffect(() => {
        options.current.disableAutoPageChange = props.disableAutoPageChange;
        forceUpdate();
    }, [props.disableAutoPageChange]);
    useEffect(() => {
        options.current.disableViewerOption = props.disableViewerOption;
        forceUpdate();
    }, [props.disableViewerOption]);
    useEffect(() => {
        options.current.disableMousePen = props.disableMousePen;
        forceUpdate();
    }, [props.disableMousePen]);
    useEffect(() => {
        options.current.surfaceOwnerIds = props.surfaceOwnerIds ? props.surfaceOwnerIds : null;
        forceUpdate();
    }, [props.surfaceOwnerIds]);
    useEffect(() => {
        options.current.writerIds = props.writerIds ? props.writerIds : null;
        forceUpdate();
    }, [props.writerIds]);
    useEffect(() => {
        options.current.pageChangerWriters = props.pageChangerWriters ? props.pageChangerWriters : null;
        forceUpdate();
    }, [props.pageChangerWriters]);
    // 2021/07/21
    useEffect(() => {
        options.current.laserPointer = props.laserPointer;
        forceUpdate();
    }, [props.laserPointer]);
    // 2021/08/15
    useEffect(() => {
        options.current.enablePlateInput = props.enablePlateInput;
        // if (props.enablePlateInput) {
        //   options.current.laserPointer = true;
        // }
        forceUpdate();
    }, [props.enablePlateInput]);
    /**
     *
     *
     * @param {number} zoom
     */
    const onMixedViewZoomChanged = (zoom) => {
        // console.log(`setGlobalZoomCallback: ${zoom}`)
        options.current.zoom = zoom;
        if (props.onZoomChanged) {
            props.onZoomChanged(zoom);
        }
        // child view인 mixed view에서 불린 callback이므로, 바로 state를 바꿀 수는 없다
        sleep(0).then(() => {
            forceUpdate();
        });
    };
    useEffect(() => {
        options.current.zoom = props.zoom;
        forceUpdate();
    }, [props.zoom]);
    /**
     *
     *
     * @param {ZoomFitEnum} viewFit
     */
    const setViewFitCallback = (viewFit) => {
        console.log(`setViewFitCallback: ${viewFit}`);
        // child view인 mixed view에서 불린 callback이므로, 바로 state를 바꿀 수는 없다
        // sleep(0).then(() => {
        //   forceUpdate();
        // });
    };
    useEffect(() => {
        options.current.fitType = props.fitType;
        forceUpdate();
    }, [props.fitType]);
    /**
     *
     *
     * @param {IPageSOBP} sobp
     */
    const onNcodePageChanged = (sobp, stroke) => {
        // console.log(`pageChange: ViewControlSample, BEFORE mixed view reported PageChanged = ${makeNPageIdStr(pageInfo)}`);
        if (!isSamePage(options.current.activePage, sobp)) {
            // console.log(`pageChange: ViewControlSample, mixed view reported PageChanged = ${makeNPageIdStr(pageInfo)}`);
            options.current.activePage = sobp;
            // child view인 mixed view에서 불린 callback이므로, 바로 state를 바꿀 수는 없다
            sleep(0).then(() => {
                forceUpdate();
            });
        }
    };
    const onViewPageChanged = (sobp) => {
        if (props.onViewPageChanged) {
            props.onViewPageChanged(sobp);
        }
    };
    /**
     *
     *
     * @param {number} width
     */
    const handlePageWidthNeeded = (width) => {
        // child view인 mixed view에서 불린 callback이므로, 바로 state를 바꿀 수는 없다
        // sleep(0).then(() => {
        //   forceUpdate();
        // });
    };
    // const inputPens = useMemo(() => {
    //   if (options.penWithStatus && options.penWithStatus.length > 0) {
    //     console.log(`virtual pens, use Memo true  ${options.penWithStatus[0].enabled}`)
    //     return options.penWithStatus.filter(item => item.enabled).map(item => item.pen);
    //   }
    //   return [];
    // }, [options.penWithStatus]);
    const toggleOptionWindow = () => {
        options.current.disableViewerOption = !options.current.disableViewerOption;
        forceUpdate();
    };
    // console.log(`pageChange: ViewControlSample: ${Math.round(props.forceDeltaTime)}sec ${makeNPageIdStr(options.activePage)}`);
    // console.log(`SAMPLE virtual pens = ${options.current.penWithStatus ? options.current.penWithStatus.length : 0} ${inputPens.length}`)
    const handleActivePensChanged = (pens) => {
        options.current.pens = pens;
        forceUpdate();
    };
    const handleOptionPanelPageChanged = (sobp) => {
        options.current.activePage = sobp;
        forceUpdate();
    };
    /**
     * 여기서 보여주는 sample은 surface owner가 1명,
     * MixedPageView에는 surface owner가 여러명 있을 수 있다.
     *
     * @param {string} owner
     */
    const handleSurfaceOwnerChanged = (owner) => {
        if (owner) {
            // 여기서 보여주는 sample은 surface owner가 1명,
            // MixedPageView에는 surface owner가 여러명 있을 수 있다. (필기 비교를 위해)
            options.current.surfaceOwnerIds = [owner];
        }
        else {
            // undefined를 넣어주면, 모든 필기를 보여준다
            options.current.surfaceOwnerIds = undefined;
        }
        forceUpdate();
    };
    /**
     * 여기서 보여주는 sample은 writer가 1명,
     * MixedPageView에는 writer가 여러명 있을 수 있다.
     *
     * @param {string} writer
     */
    const handleWriterChanged = (writer) => {
        if (writer) {
            // 여기서 보여주는 sample은 surface owner가 1명,
            // MixedPageView에는 surface owner가 여러명 있을 수 있다. (필기 비교를 위해)
            options.current.writerIds = [writer];
        }
        else {
            // undefined를 넣어주면, 모든 필기를 보여준다
            options.current.writerIds = undefined;
        }
        forceUpdate();
    };
    /**
     * 2021/08/04
     * 여기서 보여주는 sample은 writer가 1명,
     * MixedPageView에는 writer가 여러명 있을 수 있다.
     *
     * @param {string} writer
     */
    const handlePageChangerWriterChanged = (writer) => {
        if (writer) {
            // 여기서 보여주는 sample은 surface owner가 1명,
            // MixedPageView에는 surface owner가 여러명 있을 수 있다. (필기 비교를 위해)
            options.current.pageChangerWriters = [writer];
        }
        else {
            // undefined를 넣어주면, 모든 필기를 보여준다
            options.current.pageChangerWriters = undefined;
        }
        forceUpdate();
    };
    /**
     * 2021/08/04
     * manual zoom in/out
     */
    const handleZoomIn = () => {
        let scale = options.current.zoom * 1.2;
        if (scale > PDFVIEW_ZOOM_MAX)
            scale = PDFVIEW_ZOOM_MAX;
        if (scale < PDFVIEW_ZOOM_MIN)
            scale = PDFVIEW_ZOOM_MIN;
        // 2021/08/04, zoom을 바꾸기 전에, fitType은 반드시 FREE로 바꿔줘야 한다.
        // options.current.fitType이 scale에 우선하기 때문이다
        options.current.fitType = ZoomFitEnum.FREE;
        options.current.zoom = scale;
        forceUpdate();
    };
    /**
     * 2021/08/04
     * manual zoom in/out
     */
    const handleZoomOut = () => {
        let scale = options.current.zoom / 1.2;
        if (scale > PDFVIEW_ZOOM_MAX)
            scale = PDFVIEW_ZOOM_MAX;
        if (scale < PDFVIEW_ZOOM_MIN)
            scale = PDFVIEW_ZOOM_MIN;
        // 2021/08/04, zoom을 바꾸기 전에, fitType은 반드시 FREE로 바꿔줘야 한다.
        // options.current.fitType이 scale에 우선하기 때문이다
        options.current.fitType = ZoomFitEnum.FREE;
        options.current.zoom = scale;
        forceUpdate();
    };
    // 2021/08/04
    //
    // 외부 인터페이스
    const getState = (fieldName) => {
        switch (fieldName) {
            case "zoom": {
                return options.current.zoom;
            }
            case "center": {
                return options.current.center;
            }
        }
    };
    // 2021/08/25
    const { width, height, ref: resizeDetectorRef } = useResizeDetector();
    /**
     * 처음 크기가 정해지면, overlay용 sobp를 획득한다
     * 만일, overlaySobp가 주어지면 그걸 활용하도록 한다.
     */
    useEffect(() => {
        if (width && height) {
            if (!options.current.inited) {
                console.log(`${props.id} Overlay View initial size=${width}, ${height}`);
                options.current.inited = true;
                options.current.initialSize = { width, height };
                const sobp = prepareNewOverlayPage({ width: 1280, height: 720, sobp: props.overlaySobp });
                options.current.activePage = sobp;
                forceUpdate();
            }
            //   else {
            //     console.log(`${props.id} Overlay View changing size=${width}, ${height}`);
            //     options.current.initialSize = { width, height };
            //     const msi = MappingStorage.getInstance();
            //     msi.changeMappedPdfPageSize(
            //       {
            //         sobp: options.current.activePage,
            //         size_pu: {
            //           width: 1920, height: 1080,
            //         }
            //       }
            //     );
            //     forceUpdate();
            //   }
        }
    }, [width, height]);
    return (_jsxs("div", Object.assign({ id: "overlay-relative", style: {
            position: "relative",
            margin: 0,
            padding: 0,
            width: "100%",
            height: "100%",
        }, ref: resizeDetectorRef }, { children: [_jsx("div", Object.assign({ id: "overlay-absolute", style: {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    right: 0,
                    height: "100%",
                } }, { children: _jsx(MixedPageView, { id: props.id, parentName: "grida-main-home", pageInfo: options.current.activePage, basePageInfo: options.current.activePage, pens: options.current.pens, rotation: 0, isMainView: true, 
                    // fitType={options.current.fitType ? options.current.fitType : ZoomFitEnum.FULL}
                    fitType: ZoomFitEnum.FULL, padding: props.padding ? props.padding : 10, fromStorage: false, onPdfRequested: props.isOverlayView ? null : props.onPdfRequested, onPdfPageFault: props.isOverlayView ? null : props.onPdfPageFault, onNcodePageChanged: onNcodePageChanged, noMorePdfSignal: false, handlePageWidthNeeded: handlePageWidthNeeded, renderCountNo: 0, onZoomChanged: onMixedViewZoomChanged, setViewFitFn: setViewFitCallback, 
                    // 2021/08/04, to add set zoom functionality, from here
                    zoom: options.current.zoom, center: options.current.center, 
                    // to here
                    disablePanZoom: true, disableAutoPageChange: true, disableMousePen: options.current.disableMousePen, disableAutoFocus: true, showGrid: options.current.showGrid, isCropMode: options.current.isCropMode, onCropRangeChagned: props.onCropRangeChagned, cropRect: props.cropRect, onMouseDown: props.onMouseDown, onMouseMove: props.onMouseMove, onMouseUp: props.onMouseUp, onMouseWheel: props.onMouseWheel, onTouchTap: props.onTouchTap, onTouchPress: props.onTouchPress, debugInfo: props.debugInfo, laserPointer: options.current.laserPointer, enablePlateInput: options.current.enablePlateInput, playState: props.playState ? props.playState : PLAYSTATE.live, replayStorage: props.replayStorage, onReplayTimeChanged: props.onReplayTimeChanged, onReplayStateChanged: props.onReplayStateChanged, replaySpeed: props.replaySpeed, forceDeltaTime: props.forceDeltaTime, disableAnyMouseAction: props.disableAnyMouseAction, 
                    // 2021/06/13
                    surfaceOwnerIds: options.current.surfaceOwnerIds, 
                    // 2021/06/13
                    writerIds: options.current.writerIds, 
                    // 2021/08/04
                    pageChangerWriters: options.current.pageChangerWriters, storage: props.storage, onViewPageChanged: onViewPageChanged, disableScrollBar: props.disableScrollBar, fontSize: props.fontSize, isOverlayView: true, isPriorityView: props.isPriorityView }) })), props.enableOptionButton ?
                _jsxs("div", Object.assign({ id: "viewoption-absolute", style: { position: "absolute", left: 5, top: 5, right: 5, zIndex: 99, } }, { children: [options.current.disableViewerOption ? "" :
                            _jsx("div", Object.assign({ id: "viewoption-optionwindow", style: { position: "absolute", left: 0, top: 0, right: 0, } }, { children: _jsx(ViewOptionsForTest, Object.assign({}, options.current, { pageInfos: [], onSetOptions: handleOptionChanged, onReportActivePens: handleActivePensChanged, changePageFn: handleOptionPanelPageChanged, changeOwnerFn: handleSurfaceOwnerChanged, changeWriterFn: handleWriterChanged, handleZoomIn: handleZoomIn, handleZoomOut: handleZoomOut, changePageChangerWriterFn: handlePageChangerWriterChanged })) })), _jsx("div", Object.assign({ id: "viewoption-button", style: { position: "absolute", left: 0, top: 0, right: 0, zIndex: 99 } }, { children: _jsxs("button", Object.assign({ onClick: toggleOptionWindow }, { children: [" ", options.current.disableViewerOption ? "옵션창 열기" : "옵션창 숨기기", " "] })) }))] }))
                : ""] })));
});
export default OverlayInkView;
//# sourceMappingURL=OverlayInkView.js.map